<script setup lang="ts">
/**
 * Editor header: identity fields plus the actions that write the document. Save is never gated;
 * Publish is gated by the store's `isPublishable` and hidden once the row is published or archived.
 * Discipline follows from the type and is not shown.
 */

import { AppText } from '@shared/ui/typography'
import { AppSelect } from '@shared/ui/selects'
import { AppInput } from '@shared/ui/inputs'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'
import { AppBadge } from '@shared/ui/badges'

import { useAssignmentHeader } from '../../../model/assignments/assignment-header.hook'

defineOptions({
  name: 'AssignmentHeader',
})

// Off by default: a parent that forgets to bind the privilege gets a read-only header, not write controls.
withDefaults(defineProps<{
  canManage?: boolean
}>(), {
  canManage: false,
})

// Archive / restore / duplicate act on the row and live in the list's row menu.
const emit = defineEmits<{
  'click:save': []
  'click:validate': []
  'click:publish': []
}>()

const {
  refTitle,
  refGrade,
  refTypeLabel,
  refStatusLabel,
  refStatusColor,
  refIsDirty,
  refIsSaving,
  refDirtyText,
  refSaveText,
  refCanPublish,
  refPublishBlockedReason,
  refIsPublished,
  refIsArchived,
  gradeSelectOptions,
} = useAssignmentHeader()
</script>

<template>
  <div class="assignment-header">
    <!-- The card's name. `READING` is shown rather than offered (decision 8) and the badge is the
         lifecycle, so neither is a control — which is exactly why they read as a heading. -->
    <div class="assignment-header__name">
      <AppText name="body1-medium">
        {{ refTypeLabel }}
      </AppText>
      <AppBadge
        :color="refStatusColor"
        :text="refStatusLabel"
      />
    </div>

    <div class="assignment-header__row">
      <div class="assignment-header__identity">
        <!-- The CATALOGUE handle an admin picks from — distinct from the book's own name, which
             lives in the book card below. Creation seeds them identically; they diverge from
             there. -->
        <AppInput
          v-model="refTitle"
          placeholder="Title in the catalogue"
          class="assignment-header__title"
        />

        <AppSelect
          v-model="refGrade"
          :options="gradeSelectOptions"
          placeholder="Grade"
          :disabled="refIsSaving"
          class="assignment-header__grade"
        />
      </div>

      <div
        v-if="canManage"
        class="assignment-header__actions"
      >
        <!-- Immediately before Save, because that is the button it is about. -->
        <AppText
          name="body2"
          :color="refIsDirty ? 'red-600' : 'slate-500'"
        >
          {{ refDirtyText }}
        </AppText>

        <!-- Never disabled on an unvalidated document: saving a draft is not publishing it. -->
        <BtnPrimary
          :text="refSaveText"
          :disabled="refIsSaving"
          @click="emit('click:save')"
        />
        <BtnSecondary
          text="Validate"
          :disabled="refIsSaving"
          @click="emit('click:validate')"
        />
        <!-- An archived row is restored from the list, never published from here. -->
        <BtnPrimary
          v-if="!refIsPublished && !refIsArchived"
          color="emerald"
          text="Publish"
          :disabled="refIsSaving || !refCanPublish"
          @click="emit('click:publish')"
        />
      </div>
    </div>

    <!-- THE REASON, not just the disabled state. Hidden once the row is published or archived, where
         there is no Publish button for it to explain. -->
    <AppText
      v-if="canManage && !refIsPublished && !refIsArchived && refPublishBlockedReason"
      name="body2"
      color="slate-500"
    >
      {{ refPublishBlockedReason }}
    </AppText>
  </div>
</template>

<style scoped lang="scss" src="./AssignmentHeader.scss" />
